import { asStore } from '@src/application/store/as-store.ts';
import { reactive, ref } from 'vue';
import { RouteDefinition } from '@router/interfaces/route-definition.ts';
import { ResolvedRoute } from '@router/interfaces/resolved-route.ts';
import { RouterPush } from '@router/interfaces/router-push.ts';
import { closeLoader, openLoader } from '@utils/loader-util.ts';
import { Dictionary } from '@src/application/dictionary.ts';
import showSideBarStore from '@src/application/store/show-side-bar-store.ts';
import mobileDetectStore from '@src/application/store/mobile-detect-store.ts';

export function useRouterComposable() {
    const routesNameStore = asStore('router.routes', () => reactive<{ [routeName: string]: RouteDefinition }>({}));
    if (Object.keys(routesNameStore).length === 0) {
        for (const routeName in routesNameStore) {
            delete routesNameStore[routeName];
        }

        const routeFiles = import.meta.glob<{ default: RouteDefinition[] }>('@src/**/routes.*', { eager: true });

        for (const fileName in routeFiles) {
            for (const route of routeFiles[fileName].default) {
                routesNameStore[route.name] = route;
            }
        }
    }

    function resolveUrl(url: string): ResolvedRoute {
        const urlObject = new URL(url);
        const urlParts = urlObject.pathname.split('/');
        for (const routeName in routesNameStore) {
            const routeDefinition = routesNameStore[routeName];
            const routeParts = routeDefinition.path.split('/');
            let noMatch = false;
            let partPosition = 0;
            const propsParameters: { [parameter: string]: any } = {};
            for (const routePart of routeParts) {
                if (routePart.startsWith(':')) {
                    if (urlParts[partPosition] === undefined) {
                        if (routePart.endsWith('?')) {
                            continue;
                        }

                        noMatch = true;
                        break;
                    }

                    const parameterName = routePart.replace(/[^A-z0-9]/g, '');
                    propsParameters[parameterName] = decodeURIComponent(urlParts[partPosition]);
                } else if (urlParts[partPosition] !== routePart) {
                    noMatch = true;
                    break;
                }

                partPosition++;
            }

            if (urlParts[partPosition] !== undefined) {
                noMatch = true;
            }

            if (noMatch) {
                continue;
            }

            return {
                ...routeDefinition,
                params: propsParameters,
                href: url,
                query: urlObject.searchParams,
            };
        }

        return {
            path: '/404',
            meta: {
                title: '404 Not Found',
            },
            name: '404',
            component: () => import('@src/application/404.vue'),
            href: url,
        };
    }

    const currentRoute = asStore('router.currentRoute', () => ref<ResolvedRoute>(resolveUrl(window.location.toString())));
    const routerInitialised = asStore('router.initialised', () => ref(false));
    if (!routerInitialised.value) {
        window.addEventListener('popstate', () => {
            openLoader();
            currentRoute.value = resolveUrl(window.location.toString());
            closeLoader();
        });

        routerInitialised.value = true;
    }

    function resolveRoute(route: RouterPush) {
        let routeDefinition = null;
        if (route.name) {
            routeDefinition = routesNameStore[route.name];
        } else if (route.path) {
            routeDefinition = resolveUrl(route.path);
            route.params = routeDefinition.params;
        } else {
            throw new Error('neither path nor name given to router push');
        }

        const uriParts = [];
        const validParams: Dictionary<string|number|boolean> = {};
        for (const routePart of routeDefinition.path.split('/')) {
            if (!routePart.startsWith(':')) {
                uriParts.push(routePart);
                continue;
            }

            const parameterName = routePart.replace(/[^A-z0-9]/g, '');
            if (route.params === undefined || route.params[parameterName] === undefined || route.params[parameterName] === null) {
                if (routePart.endsWith('?')) {
                    continue;
                }

                throw new Error('missing required parameter: ' + parameterName);
            }

            const parameterValue = route.params[parameterName];
            if (parameterValue === null || parameterValue === undefined) {
                continue;
            }
            uriParts.push(encodeURIComponent(parameterValue));
            validParams[parameterName] = parameterValue;
        }

        const queryParams = new URLSearchParams();
        for (const parameterName in route.params) {
            if (
                Object.keys(validParams)
                    .includes(parameterName)
            ) {
                continue;
            }

            if (route.params[parameterName] === undefined || route.params[parameterName] === null) {
                continue;
            }

            queryParams.set(parameterName, route.params[parameterName].toString());
        }

        let href = uriParts.join('/');
        if (queryParams.size > 0) {
            href += '?' + queryParams.toString();
        }

        return {
            ...routeDefinition,
            params: validParams,
            href,
            query: queryParams,
        };
    }

    function pushRoute(route: RouterPush, openInNewTab: boolean = false) {
        const resolvedRoute = resolveRoute(route);
        if (openInNewTab) {
            window.open(resolvedRoute.href, '_blank');
            return;
        }

        openLoader();
        if (mobileDetectStore.isMobile.data) {
            showSideBarStore.showSideBar.data = false;
        }

        currentRoute.value = resolvedRoute;
        history.pushState({}, '', resolvedRoute.href);
        closeLoader();
    }

    function pushParams(params: Dictionary<string|number|boolean|undefined|null>, openInNewTab: boolean = false) {
        const resolvedRoute = resolveRoute({
            ...currentRoute.value,
            params: {
                ...currentRoute.value.params,
                ...params,
            },
        });

        if (openInNewTab) {
            window.open(resolvedRoute.href, '_blank');
            return;
        }

        if (currentRoute.value.href === resolvedRoute.href) {
            return;
        }

        currentRoute.value.params = resolvedRoute.params;
        currentRoute.value.href = resolvedRoute.href;
        currentRoute.value.query = resolvedRoute.query;
        history.pushState({}, '', resolvedRoute.href);
    }

    function currentRouteHasParam(param: string) {
        return currentRoute.value.path.includes(':' + param);
    }

    function historyPushParams(params: Dictionary<string|number|boolean|undefined|null>) {
        const resolvedRoute = resolveRoute({
            ...currentRoute.value,
            params: {
                ...currentRoute.value.params,
                ...params,
            },
        });

        history.pushState({}, '', resolvedRoute.href);
    }

    function getRouteByRouteName(routeName: string): RouteDefinition|null {
        return routesNameStore[routeName] ?? null;
    }

    return {
        pushRoute,
        push: pushRoute,
        pushParams,
        historyPushParams,
        resolveRoute,
        currentRouteHasParam,
        resolveUrl,
        currentRoute,
        getRouteByRouteName,
    };
}
